
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Virtual Accounts',
  title: 'Payouts',
  description: 'Send funds from your virtual account'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`A payout represents the flow of funds from a client's virtual account to an end user. A payout can be for an arbitrary amount if you have sufficient funds stored in your virtual account to cover the payout. Using Payouts requires integrating with virtual account API routes and receiving transaction webhooks.`}</p>
    <h4 {...{
      "id": "key-benefits-of-payouts"
    }}>{`Key Benefits of Payouts`}</h4>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Instant`}</strong>{`: Payouts ensures that funds arrive in your users' accounts almost immediately.`}</li>
      <li parentName="ul"><strong parentName="li">{`Easy to Integrate`}</strong>{`: A single, unified API for both Payment Initiation and Payouts streamlines the development process.`}</li>
      <li parentName="ul"><strong parentName="li">{`Verified`}</strong>{`: In conjunction with Plaid's Auth and Identity products, users' bank details are securely and automatically populated. This pre-verification step ensures you are always sending funds to a valid and correct bank account, minimising payment failures and fraud risk.`}</li>
      <li parentName="ul"><strong parentName="li">{`Low Cost`}</strong>{`: Move away from the expensive and variable fees associated with card payments. Payouts offers a low, fixed-fee structure, allowing you to significantly reduce costs.`}</li>
    </ul>
    <h4 {...{
      "id": "execute-a-payout"
    }}>{`Execute a Payout`}</h4>
    <p>{`Make sure your virtual account is set up before following these steps. For more information on setting up an account, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/managing-virtual-accounts"
      }}>{`Managing virtual accounts`}</a>{`.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/wallet/get`}</inlineCode>{` to check your virtual account balance (optional).`}</p>
        <ul parentName="li">
          <li parentName="ul">{`If you have insufficient funds to make your desired payout, make sure to `}<a parentName="li" {...{
              "href": "/docs/payment-initiation/virtual-accounts/managing-virtual-accounts/#fund-your-virtual-account"
            }}>{`fund your virtual account`}</a>{` before proceeding. After funding your virtual account, another request to `}<inlineCode parentName="li">{`/wallet/get`}</inlineCode>{` will show the updated balance.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` to get a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` representing the recipient of the payout.`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li">{`Provide a unique `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` and an `}<inlineCode parentName="p">{`identity`}</inlineCode>{` object containing the recipient's `}<inlineCode parentName="p">{`name`}</inlineCode>{` plus at least one of `}<inlineCode parentName="p">{`emails`}</inlineCode>{` or `}<inlineCode parentName="p">{`phone_numbers`}</inlineCode>{`. All other fields in the identity object are optional.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Calling `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` again with the same `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` returns the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, so you can re-use it for future payouts to the same recipient. For more information, see `}<a parentName="p" {...{
                "href": "/docs/api/users/"
              }}>{`User APIs`}</a>{`.`}</p>
          </li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/wallet/transaction/execute`}</inlineCode>{` with the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` and store the `}<inlineCode parentName="p">{`transaction_id`}</inlineCode>{` and status from the response.`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><a parentName="p" {...{
                "href": "https://dashboard.plaid.com/developers/webhooks"
              }}>{`Configure transaction webhooks`}</a>{` to receive real-time `}<a parentName="p" {...{
                "href": "/docs/api/products/virtual-accounts/#wallet_transaction_status_update"
              }}>{`status update webhooks`}</a>{` for each payout transaction.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`In addition to using webhooks, you can confirm the transaction has been executed by calling `}<inlineCode parentName="p">{`/wallet/transaction/get`}</inlineCode>{` with the `}<inlineCode parentName="p">{`transaction_id`}</inlineCode>{`.`}</p>
          </li>
        </ul>
      </li>
    </ol>
    <h4 {...{
      "id": "verification-of-payee"
    }}>{`Verification of Payee`}</h4>
    <p>{`Verification of Payee is a regulatory requirement under the EU Instant Payments Regulation. It requires that PSPs check the recipient's name against the IBAN and return the result to the payer before execution, reducing fraud and user error. Plaid automatically performs this check for all Eurozone payouts.`}</p>
    <h5 {...{
      "id": "verification-statuses"
    }}>{`Verification statuses`}</h5>
    <p>{`The payee verification check returns one of five statuses: Match, Partial Match, No Match, Error (indicating that an error occurred during payee verification), and Match not possible (indicating that the verification system is unavailable). By default, payments proceed for all statuses except No Match. You can also customise the product behaviour for the Partial Match and No Match statuses by contacting your account manager.`}</p>
    <p>{`If the payment is blocked:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode>{` webhook will indicate a failed payment.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Calling `}<inlineCode parentName="p">{`/wallet/transaction/get`}</inlineCode>{` with the `}<inlineCode parentName="p">{`transaction_id`}</inlineCode>{` will show the `}<inlineCode parentName="p">{`TRANSACTION_PAYEE_VERIFICATION_NO_MATCH`}</inlineCode>{` error.`}</p>
      </li>
    </ul>
    <h4 {...{
      "id": "testing-payouts"
    }}>{`Testing Payouts`}</h4>
    <p>{`You can begin testing Payouts in Sandbox by following the steps listed in the `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/add-to-app"
      }}>{`Add Virtual Accounts to your App`}</a>{` guide. For Production access you will first need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
      }}>{`submit a product access request support ticket`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "key-benefits-of-payouts",
        level: 4,
        title: "Key Benefits of Payouts",
      },
{
        id: "execute-a-payout",
        level: 4,
        title: "Execute a Payout",
      },
{
        id: "verification-of-payee",
        level: 4,
        title: "Verification of Payee",
      },
{
        id: "testing-payouts",
        level: 4,
        title: "Testing Payouts",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
